import { Menu, Search, X, ArrowRight, LogOut } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';

import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { useAuthContext } from '@/contexts/AuthContext';
import linkLogo from '@/assets/link-logo-clean.png';


const LandingNav = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { isAuthenticated, signOut } = useAuthContext();
  const navigate = useNavigate();

  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  const searchPages = [
    { label: 'Platform', href: '/platform', keywords: 'platform closed loop revenue integrity pillars' },
    { label: 'Solutions', href: '/solutions', keywords: 'solutions health system surgeon segment' },
    { label: 'Surgeon Console', href: '/surgeon', keywords: 'surgeon console dictation operative note post-op' },
    { label: 'Company', href: '/company', keywords: 'company about founder larkin legacy' },
    { label: 'Resources', href: '/resources', keywords: 'resources whitepaper videos roi calculator security' },
    { label: 'Dashboard', href: '/dashboard', keywords: 'dashboard claims analytics kpi' },
    { label: 'Sign In', href: '/auth', keywords: 'sign in login auth register' },
  ];

  const filteredPages = searchQuery.length > 0
    ? searchPages.filter(p =>
        p.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.keywords.includes(searchQuery.toLowerCase())
      )
    : searchPages;

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const navLinks = [
    { label: 'Platform', href: '/platform' },
    { label: 'Solutions', href: '/solutions' },
    { label: 'Surgeon Console', href: '/surgeon' },
    { label: 'Company', href: '/company' },
    { label: 'Resources', href: '/resources' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50">
      {/* Solid white bg with subtle bottom border */}
      <div className="absolute inset-0 bg-card border-b border-border" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Left: Brand Name */}
          <Link to="/" className="hidden md:flex items-center min-h-[44px]">
            <img src={linkLogo} alt="LINK" className="h-12" />
          </Link>

          {/* Center: Nav Links */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                className="text-sm font-semibold text-muted-foreground hover:text-cta transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right: Auth Actions */}
          <div className="hidden md:flex items-center gap-4">
            {isAuthenticated ? (
              <button
                onClick={handleSignOut}
                className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-cta transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </button>
            ) : (
              <>
                <Link
                  to="/auth"
                  className="text-sm font-semibold text-muted-foreground hover:text-cta transition-colors"
                >
                  Sign In
                </Link>
                <Link to="/auth">
                  <Button className="bg-cta hover:bg-cta/90 text-cta-foreground font-semibold px-5 py-2.5 rounded-md text-sm">
                    Book Strategy Call
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile */}
          <div className="flex md:hidden items-center justify-between w-full">
            <Link to="/" className="flex items-center min-h-[44px]">
              <img src={linkLogo} alt="LINK" className="h-10" />
            </Link>

            <div className="flex items-center gap-1">
              <button
                onClick={() => setSearchOpen(true)}
                className="text-muted-foreground hover:text-foreground transition-colors p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
              >
                <Search className="w-5 h-5" strokeWidth={1.5} />
              </button>

              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <button className="text-muted-foreground hover:text-foreground transition-colors p-2 min-h-[44px] min-w-[44px] flex items-center justify-center">
                    <Menu className="w-6 h-6" strokeWidth={1.5} />
                  </button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[300px] bg-card border-l border-border p-0">
                  <div className="flex flex-col h-full">
                    <div className="p-4 border-b border-border">
                      <img src={linkLogo} alt="LINK" className="h-10" />
                    </div>
                    <div className="flex-1 py-4 px-4 space-y-1">
                      {navLinks.map((link) => (
                        <Link
                          key={link.label}
                          to={link.href}
                          className="block text-sm font-semibold text-muted-foreground hover:text-cta transition-colors py-3 px-4 rounded hover:bg-muted min-h-[44px] flex items-center"
                          onClick={() => setMobileOpen(false)}
                        >
                          {link.label}
                        </Link>
                      ))}
                    </div>
                    <div className="p-4 border-t border-border space-y-3">
                      <Link to="/auth" onClick={() => setMobileOpen(false)}>
                        <Button variant="outline" className="w-full h-11 border-border text-foreground hover:bg-muted">
                          Sign In
                        </Button>
                      </Link>
                      <Link to="/auth" onClick={() => setMobileOpen(false)}>
                        <Button className="w-full h-11 bg-cta hover:bg-cta/90 text-cta-foreground font-semibold mt-2">
                          Book Strategy Call
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>

      {/* Search Overlay */}
      {searchOpen && (
        <div className="fixed inset-0 z-[60] bg-card/98 backdrop-blur-sm flex flex-col">
          <div className="flex items-center gap-3 px-6 py-4 border-b border-border max-w-3xl mx-auto w-full">
            <Search className="w-5 h-5 text-muted-foreground shrink-0" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search pages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent text-foreground text-base placeholder:text-muted-foreground/50 outline-none"
            />
            <button
              onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
              className="text-muted-foreground hover:text-foreground p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-4 max-w-3xl mx-auto w-full space-y-1">
            {filteredPages.map((page) => (
              <Link
                key={page.href}
                to={page.href}
                onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
                className="flex items-center gap-3 px-4 py-3 rounded text-muted-foreground hover:text-cta hover:bg-muted transition-colors min-h-[44px]"
              >
                <ArrowRight className="w-4 h-4 shrink-0" />
                <span className="text-sm font-medium">{page.label}</span>
              </Link>
            ))}
            {filteredPages.length === 0 && (
              <p className="text-center text-muted-foreground py-8 text-sm">No pages found</p>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default LandingNav;
